import { execSync } from 'node:child_process';
import { existsSync, mkdirSync, readFileSync, rmSync, unlinkSync, writeFileSync } from 'node:fs';
import { connect } from 'node:net';
import { homedir } from 'node:os';
import { dirname, join, resolve } from 'node:path';
import { fileURLToPath } from 'node:url';
import { daemonLogPath, daemonUpdatingPath, globalDir, socketPath } from '../shared/paths.js';
import { type SetupResult, removeTmuxKeybind, setupTmuxKeybind } from './tmux-setup.js';
import { ensureRequiredCrtrPlugins, ensureRequiredPlugins, ensureSisyphusPluginInstalled, type SisyphusPluginInfo } from './plugins.js';

const PLIST_LABEL = 'com.sisyphus.daemon';
const PLIST_FILENAME = `${PLIST_LABEL}.plist`;

function launchAgentDir(): string {
  return join(homedir(), 'Library', 'LaunchAgents');
}

function plistPath(): string {
  return join(launchAgentDir(), PLIST_FILENAME);
}

function daemonBinPath(): string {
  // In bundled output, cli.js and daemon.js are siblings in dist/
  const installDir = dirname(fileURLToPath(import.meta.url));
  return resolve(installDir, 'daemon.js');
}

function generatePlist(nodePath: string, daemonPath: string, logPath: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
  <key>Label</key>
  <string>${PLIST_LABEL}</string>
  <key>ProgramArguments</key>
  <array>
    <string>${nodePath}</string>
    <string>${daemonPath}</string>
  </array>
  <key>RunAtLoad</key>
  <true/>
  <key>KeepAlive</key>
  <true/>
  <key>StandardOutPath</key>
  <string>${logPath}</string>
  <key>StandardErrorPath</key>
  <string>${logPath}</string>
</dict>
</plist>
`;
}

export function isInstalled(): boolean {
  return existsSync(plistPath());
}

export async function ensureDaemonInstalled(): Promise<void> {
  if (process.platform !== 'darwin') return;

  // Idempotent: ensures the user-facing slash-command plugin is available.
  // Placed outside the !isInstalled() guard so users whose daemon was installed
  // before this plugin shipped self-heal the next time the daemon is unreachable.
  const sisyphusPlugin = ensureSisyphusPluginInstalled();

  // Also idempotent: required plugins from config (e.g. devcore@crouton-kit).
  // Must run on every daemon-ensure, not just first install — users whose
  // plist predates a new entry in `requiredPlugins` would otherwise never
  // get it.
  await ensureRequiredPlugins(process.cwd());

  // Same idempotent rationale for the crtr ecosystem: registers the sisyphus
  // crtr marketplace + plugin so prompts' `crtr skill show sisyphus/*` resolve.
  // Best-effort — never blocks daemon install.
  ensureRequiredCrtrPlugins(process.cwd());

  if (!isInstalled()) {
    const nodePath = process.execPath;
    const daemonPath = daemonBinPath();
    const logPath = daemonLogPath();

    mkdirSync(globalDir(), { recursive: true });
    mkdirSync(launchAgentDir(), { recursive: true });

    const plist = generatePlist(nodePath, daemonPath, logPath);
    writeFileSync(plistPath(), plist, 'utf8');

    execSync(`launchctl load -w ${plistPath()}`);

    const keybindResult = await setupTmuxKeybind();

    printGettingStarted(keybindResult, sisyphusPlugin);
  }

  await waitForDaemon();
}

export async function uninstallDaemon(purge: boolean): Promise<void> {
  if (process.platform !== 'darwin') {
    console.log('Auto-install is only supported on macOS.');
    return;
  }

  const plist = plistPath();
  if (existsSync(plist)) {
    try {
      execSync(`launchctl unload -w ${plist}`, { stdio: 'pipe' });
    } catch {
      // already unloaded or not registered — ignore
    }
    unlinkSync(plist);
    console.log('Daemon unloaded and plist removed.');
  } else {
    console.log('Daemon is not installed (plist not found).');
  }

  removeTmuxKeybind();

  if (purge) {
    const dir = globalDir();
    if (existsSync(dir)) {
      rmSync(dir, { recursive: true, force: true });
      console.log(`Removed ${dir}`);
    }
  }
}

function printGettingStarted(
  keybindResult: SetupResult,
  sisyphusPlugin: SisyphusPluginInfo,
): void {
  const lines = [
    '',
    'Sisyphus installed — daemon running via launchd.',
    '',
  ];

  if (keybindResult.status === 'installed') {
    lines.push(`Tmux keybind: ${keybindResult.message}`, '');
  } else if (keybindResult.status === 'conflict' || keybindResult.status === 'requires-force') {
    lines.push(`Keybind: ${keybindResult.message}`, '');
    lines.push('Run `sis admin check check-keybinds` to see options, then `sis admin install setup --force`.', '');
  } else if (keybindResult.status === 'conf-modification-declined') {
    lines.push(keybindResult.message, '');
  }

  if (sisyphusPlugin.installed && sisyphusPlugin.autoInstalled) {
    lines.push(`Sisyphus plugin installed: sisyphus@sisyphus → ${sisyphusPlugin.installPath}`, '');
  } else if (!sisyphusPlugin.installed) {
    lines.push('Sisyphus plugin: failed to install (run `sis admin install setup` to retry; needs `claude` CLI)', '');
  }

  lines.push(
    'Run `sis ui guide` for a complete usage guide.',
    '',
  );

  console.log(lines.join('\n'));
}

function testConnection(): Promise<void> {
  return new Promise((resolve, reject) => {
    const sock = connect(socketPath());
    sock.on('connect', () => { sock.destroy(); resolve(); });
    sock.on('error', (err) => { sock.destroy(); reject(err); });
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function waitForDaemon(maxWaitMs = 6000): Promise<void> {
  const start = Date.now();
  let updatingLogged = false;

  while (Date.now() - start < maxWaitMs) {
    // Extend timeout if daemon is updating
    const updatingPath = daemonUpdatingPath();
    if (existsSync(updatingPath)) {
      if (!updatingLogged) {
        try {
          const version = readFileSync(updatingPath, 'utf-8').trim();
          console.log(`Updating sisyphus to ${version}...`);
        } catch {
          console.log('Updating sisyphus...');
        }
        updatingLogged = true;
      }
      maxWaitMs = Math.max(maxWaitMs, 30000);
    }

    if (existsSync(socketPath())) {
      try {
        await testConnection();
        return;
      } catch {
        // not ready yet
      }
    }
    await sleep(300);
  }
  throw new Error(`Daemon did not start within ${maxWaitMs}ms. Check ${daemonLogPath()}`);
}
